import ServiceNameWithPromos from "../components/ServiceNameWithPromos";
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  getCatalogoServiziFornitore,
  getMieiServizi,
  addMioServizio,
  removeMioServizio,
  getMieTariffe,
  updateMieTariffe,
} from "../Admin/adminApi";
import FasceOrarie from "../Admin/FasceOrarie";
import FornitoreChiusure from "../components/FornitoreChiusure";
import ui from "./ServiziCatalogoShared.module.css";

export default function FornitoreServizi() {
  const token = localStorage.getItem("token");

  const [sortBy, setSortBy] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = Number(searchParams.get("tab"));
  const qFromUrl = searchParams.get("q") || "";

  const [catalogo, setCatalogo] = useState([]);
  const [miei, setMiei] = useState([]);
  const [filter, setFilter] = useState(qFromUrl);

  const [tab, setTab] = useState(
    [0, 1, 2, 3].includes(tabFromUrl) ? tabFromUrl : 2
  );

  const [priceModal, setPriceModal] = useState({
    open: false,
    servizioId: null,
  });
  const [priceValue, setPriceValue] = useState("");
  const [leadTimeValue, setLeadTimeValue] = useState("");
  const [logisticsFeeExempt, setLogisticsFeeExempt] = useState(false);
  const [logisticsFee, setLogisticsFee] = useState(2.0);

  const associatiMap = useMemo(() => {
    const m = new Map();
    miei.forEach((s) => m.set(s.servizio_id || s.id_servizio || s.id, s));
    return m;
  }, [miei]);

  useEffect(() => {
    (async () => {
      try {
        const [cat, mine] = await Promise.all([
          getCatalogoServiziFornitore(token),
          getMieiServizi(token),
        ]);

        const normalizedCat = (Array.isArray(cat) ? cat : []).map((c) => ({
          id: Number(c.id),
          descrizione: c.descrizione,
          descrizione_estesa: c.descrizione_estesa,
          immagine: c.immagine,
          tipo_id: c.id_categoria_servizio,
        }));
        setCatalogo(normalizedCat);

        const normMine = (Array.isArray(mine) ? mine : []).map((s) => ({
          servizio_id: Number(s.servizio_id ?? s.id_servizio ?? s.id),
          descrizione: s.descrizione,
          prezzo: s.prezzo,
          lead_time_min: s.lead_time_min ?? null,
          active_quantity_promos: s.active_quantity_promos || [],
        }));
        setMiei(normMine);

        const f = await getMieTariffe(token);
        if (f?.logistics_fee != null) { setLogisticsFee(Number(f.logistics_fee)); setLogisticsFeeExempt(f.logistics_fee_exempt === true); }
      } catch (e) {
        console.error(e);
        toast.error("Errore nel caricamento servizi");
      }
    })();
  }, [token]);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", String(tab));
    next.set("q", filter);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, filter]);

  const saveMyFee = async () => {
    const v = Math.max(2, Number(logisticsFee || 0));
    try {
      const r = await updateMieTariffe(v, token);
      setLogisticsFee(Number(r.logistics_fee));
      toast.success("Tariffa aggiornata");
    } catch (e) {
      toast.error(e.message || "Errore salvataggio tariffa");
    }
  };

  const handleSort = (col) => {
    if (sortBy === col) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortBy(col);
      setSortDir("asc");
    }
  };

  const rows = useMemo(() => {
    const term = filter.trim().toLowerCase();
    const filtered = catalogo.filter(
      (c) =>
        (!tab || c.tipo_id === tab) &&
        (!term ||
          (c.descrizione || "").toLowerCase().includes(term) ||
          (c.descrizione_estesa || "").toLowerCase().includes(term))
    );

    if (!sortBy) return filtered;

    return [...filtered].sort((a, b) => {
      let va;
      let vb;

      if (sortBy === "descrizione") {
        va = a.descrizione || "";
        vb = b.descrizione || "";
      } else if (sortBy === "prezzo") {
        va = associatiMap.get(a.id)?.prezzo ?? 0;
        vb = associatiMap.get(b.id)?.prezzo ?? 0;
      } else if (sortBy === "lead") {
        va = associatiMap.get(a.id)?.lead_time_min ?? Number.POSITIVE_INFINITY;
        vb = associatiMap.get(b.id)?.lead_time_min ?? Number.POSITIVE_INFINITY;
      } else {
        va = 0;
        vb = 0;
      }

      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [catalogo, filter, tab, sortBy, sortDir, associatiMap]);

  const openPriceModal = (servizioId, defaultPrice = "", defaultLead = "0") => {
    setPriceModal({ open: true, servizioId });
    setPriceValue(defaultPrice);
    setLeadTimeValue(defaultLead);
  };

  const closePriceModal = () => {
    setPriceModal({ open: false, servizioId: null });
    setPriceValue("");
    setLeadTimeValue("");
  };

  const confirmAssociate = async () => {
    const servizioId = priceModal.servizioId;
    const p = Number(String(priceValue).replace(",", "."));
    if (!Number.isFinite(p) || p <= 0) {
      toast.error("Prezzo non valido");
      return;
    }

    const ltRaw = String(leadTimeValue).trim();
    const lt = parseInt(ltRaw, 10);
    if (!Number.isFinite(lt) || lt < 0) {
      toast.error("Tempo di lavorazione non valido (>= 0)");
      return;
    }

    try {
      const created = await addMioServizio(servizioId, p, token, {
        lead_time_min: lt,
      });

      const id = Number(created.servizio_id ?? created.id_servizio ?? servizioId);
      const descr =
        created.descrizione ||
        catalogo.find((c) => c.id === id)?.descrizione ||
        "Servizio";

      setMiei((prev) => {
        const previous = prev.find(s => Number(s.servizio_id) === id);
        const next = prev.filter(
          (s) => (s.servizio_id || s.id_servizio || s.id) !== id
        );
        next.push({
          servizio_id: id,
          descrizione: descr,
          active_quantity_promos: created.active_quantity_promos ?? previous?.active_quantity_promos ?? [],
          prezzo: created.prezzo ?? p,
          lead_time_min:
            created.lead_time_min != null ? created.lead_time_min : lt,
        });
        return next;
      });

      toast.success("Servizio salvato");
      closePriceModal();
    } catch (e) {
      console.error(e);
      toast.error("Operazione non riuscita");
    }
  };

  const handleToggle = async (servizioId, current) => {
    try {
      if (current) {
        await removeMioServizio(servizioId, token);
        setMiei((prev) =>
          prev.filter(
            (s) => (s.servizio_id || s.id_servizio || s.id) !== servizioId
          )
        );
        toast.success("Servizio rimosso");
      } else {
        openPriceModal(servizioId);
      }
    } catch (e) {
      console.error(e);
      toast.error("Operazione non riuscita");
    }
  };

  const handleChangePrezzo = (servizioId) => {
    const current = associatiMap.get(servizioId);
    openPriceModal(
      servizioId,
      String(current?.prezzo ?? ""),
      current?.lead_time_min != null ? String(current.lead_time_min) : "0"
    );
  };

  const sortArrow = (col) =>
    sortBy === col ? (sortDir === "asc" ? " ▲" : " ▼") : "";

  return (
    <div className={ui.page}>
      <header className={ui.header}>
        <div className={ui.headerLeft}>
          <div className={ui.titleBlock}>
            <h2 className={ui.hTitle}>I miei servizi</h2>
            <p className={ui.hSub}>Tariffa, fasce orarie e catalogo</p>
          </div>
        </div>
      </header>

      <div className={ui.card}>
        <div className={ui.cardHead}>
          <h3 className={ui.cardTitle}>Tariffa ritiro + consegna</h3>
        </div>
        <div className={ui.cardBody}>
          <div className={ui.feeGrid}>
            <div>
              {logisticsFeeExempt && <p>Esenzione amministrativa attiva: trasporto cliente gratuito. La tariffa qui sotto resta per la riattivazione.</p>}
              <label className={ui.label}>Importo (min 2,00€)</label>
              <input
                type="number"
                min="2"
                step="0.10"
                value={logisticsFee}
                onChange={(e) => {
                  const raw = String(e.target.value).replace(",", ".");
                  const parsed = parseFloat(raw);
                  if (Number.isNaN(parsed)) setLogisticsFee(2);
                  else {
                    setLogisticsFee(
                      Math.round(Math.max(2, parsed) * 100) / 100
                    );
                  }
                }}
                className={ui.input}
              />
              <div className={ui.hint}>Minimo 2,00€</div>
            </div>
            <button onClick={saveMyFee} className={ui.btnPrimary}>
              Salva
            </button>
          </div>
        </div>
      </div>

      <div className={ui.card}>
        <div className={ui.cardHead}>
          <h3 className={ui.cardTitle}>Le mie fasce orarie</h3>
        </div>
        <div className={ui.cardBody}>
          <FasceOrarie title=" " />
        </div>
      </div>

      <div className={ui.card}>
        <div className={ui.cardHead}>
          <h3 className={ui.cardTitle}>Periodi di chiusura</h3>
        </div>
        <div className={ui.cardBody}>
          <FornitoreChiusure
            token={token}
            ui={ui}
            mode="self"
            title="Le mie chiusure"
          />
        </div>
      </div>

      <div className={ui.card}>
        <div className={ui.cardHead}>
          <div className={ui.actionRow}>
            <div
              className={ui.pills}
              role="tablist"
              aria-label="Categoria servizi"
            >
              <button
                className={`${ui.pill} ${tab === 0 ? ui.pillActive : ""}`}
                onClick={() => setTab(0)}
                role="tab"
                aria-selected={tab === 0}
              >
                Tutti
              </button>
              <button
                className={`${ui.pill} ${tab === 1 ? ui.pillActive : ""}`}
                onClick={() => setTab(1)}
                role="tab"
                aria-selected={tab === 1}
              >
                Stiratura
              </button>
              <button
                className={`${ui.pill} ${tab === 2 ? ui.pillActive : ""}`}
                onClick={() => setTab(2)}
                role="tab"
                aria-selected={tab === 2}
              >
                Lavaggio
              </button>
              <button
                className={`${ui.pill} ${tab === 3 ? ui.pillActive : ""}`}
                onClick={() => setTab(3)}
                role="tab"
                aria-selected={tab === 3}
              >
                Entrambi
              </button>
            </div>

            <div className={ui.actionRight}>
              <input
                className={`${ui.input} ${ui.search}`}
                placeholder="Cerca nel catalogo…"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className={ui.cardBody}>
          <div className={ui.tableDesktop}>
            <div className={ui.tableWrap}>
              <table className={ui.table}>
                <colgroup>
                  <col className={ui.colService} />
                  <col className={ui.colPrice} />
                  <col className={ui.colLead} />
                  <col className={ui.colActions} />
                </colgroup>
                <thead>
                  <tr>
                    <th
                      className={`${ui.th} ${ui.thService}`}
                      onClick={() => handleSort("descrizione")}
                    >
                      Servizio{sortArrow("descrizione")}
                    </th>
                    <th
                      className={`${ui.th} ${ui.thNum}`}
                      onClick={() => handleSort("prezzo")}
                    >
                      Prezzo{sortArrow("prezzo")}
                    </th>
                    <th
                      className={`${ui.th} ${ui.thNum}`}
                      onClick={() => handleSort("lead")}
                    >
                      Tempo lav. (min){sortArrow("lead")}
                    </th>
                    <th className={`${ui.th} ${ui.thActions}`}>
                      Azioni
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {rows.map((row) => {
                    const associato = associatiMap.has(row.id);
                    const mine = associatiMap.get(row.id);

                    return (
                      <tr key={row.id} className={associato ? ui.rowOn : ""}>
                        <td className={`${ui.td} ${ui.tdService}`}>
                          <div className={ui.serviceCell}>
                            <ServiceNameWithPromos name={row.descrizione} className={ui.serviceName} promos={mine?.active_quantity_promos} />
                            {row.descrizione_estesa ? (
                              <span className={ui.serviceSub}>
                                {row.descrizione_estesa}
                              </span>
                            ) : null}
                          </div>
                        </td>

                        <td className={`${ui.td} ${ui.tdNum}`}>
                          {associato
                            ? `€ ${Number(mine?.prezzo ?? 0).toFixed(2)}`
                            : "—"}
                        </td>

                        <td className={`${ui.td} ${ui.tdNum}`}>
                          {associato && mine?.lead_time_min != null
                            ? `${mine.lead_time_min}`
                            : "—"}
                        </td>

                        <td className={`${ui.td} ${ui.tdActions}`}>
                          <div className={ui.actions}>
                            {associato ? (
                              <>
                                <button
                                  onClick={() => handleChangePrezzo(row.id)}
                                  className={ui.btnSecondary}
                                >
                                  Modifica
                                </button>
                                <button
                                  onClick={() => handleToggle(row.id, true)}
                                  className={ui.btnDanger}
                                >
                                  Rimuovi
                                </button>
                              </>
                            ) : (
                              <button
                                onClick={() => openPriceModal(row.id, "", "0")}
                                className={ui.btnPrimary}
                              >
                                Associa
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className={ui.mobileList}>
            {rows.map((row) => {
              const associato = associatiMap.has(row.id);
              const mine = associatiMap.get(row.id);

              return (
                <article
                  key={row.id}
                  className={`${ui.mobileCard} ${associato ? ui.mobileCardOn : ""}`}
                >
                  <div className={ui.mobileCardTop}>
                    <div className={ui.mobileTitleWrap}>
                      <ServiceNameWithPromos name={row.descrizione} className={ui.mobileTitle} promos={mine?.active_quantity_promos} />
                      {row.descrizione_estesa ? (
                        <p className={ui.mobileSub}>{row.descrizione_estesa}</p>
                      ) : null}
                    </div>
                    <span
                      className={`${ui.mobileBadge} ${associato ? ui.mobileBadgeOn : ui.mobileBadgeOff
                        }`}
                    >
                      {associato ? "Associato" : "Non associato"}
                    </span>
                  </div>

                  <div className={ui.mobileStats}>
                    <div className={ui.mobileStat}>
                      <span className={ui.mobileStatLabel}>Prezzo</span>
                      <span className={ui.mobileStatValue}>
                        {associato
                          ? `€ ${Number(mine?.prezzo ?? 0).toFixed(2)}`
                          : "—"}
                      </span>
                    </div>
                    <div className={ui.mobileStat}>
                      <span className={ui.mobileStatLabel}>Tempo lav.</span>
                      <span className={ui.mobileStatValue}>
                        {associato && mine?.lead_time_min != null
                          ? `${mine.lead_time_min} min`
                          : "—"}
                      </span>
                    </div>
                  </div>

                  <div className={ui.mobileActions}>
                    {associato ? (
                      <>
                        <button
                          onClick={() => handleChangePrezzo(row.id)}
                          className={ui.btnSecondary}
                        >
                          Modifica
                        </button>
                        <button
                          onClick={() => handleToggle(row.id, true)}
                          className={ui.btnDanger}
                        >
                          Rimuovi
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => openPriceModal(row.id, "", "0")}
                        className={ui.btnPrimary}
                      >
                        Associa
                      </button>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </div>

      {priceModal.open && (
        <div className={ui.modalBackdrop} onClick={closePriceModal}>
          <div className={ui.modal} onClick={(e) => e.stopPropagation()}>
            <div className={ui.modalHead}>
              <h3 className={ui.modalTitle}>Imposta prezzo e tempo</h3>
              <button onClick={closePriceModal} className={ui.modalClose}>
                ✕
              </button>
            </div>

            <div className={ui.modalBody}>
              <div>
                <label className={ui.label}>Prezzo (€)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={priceValue}
                  onChange={(e) => setPriceValue(e.target.value)}
                  placeholder="€ 10.00"
                  className={ui.input}
                />
              </div>

              <div>
                <label className={ui.label}>Tempo di lavorazione (min)</label>
                <input
                  type="number"
                  step="1"
                  min="0"
                  required
                  value={leadTimeValue}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === "") setLeadTimeValue("");
                    else {
                      setLeadTimeValue(
                        String(Math.max(0, parseInt(v, 10) || 0))
                      );
                    }
                  }}
                  placeholder="0 = immediato"
                  className={ui.input}
                />
                <div className={ui.hint}>Obbligatorio</div>
              </div>
            </div>

            <div className={ui.modalActions}>
              <button onClick={closePriceModal} className={ui.btnSecondary}>
                Annulla
              </button>
              <button onClick={confirmAssociate} className={ui.btnPrimary}>
                Conferma
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}